import { faCaretRight } from '@fortawesome/pro-solid-svg-icons'
import { Flex, Skeleton, Text, type TextProps } from '@mantine/core'
import { type ReactNode } from 'react'

import { type ParameterID } from '@flecto/types'
import { useItemCategoriesInfo } from '@flecto/utils'

import { Icon } from '../Icon/Icon'

export type TextItemCategoryProps = TextProps & {
  categoryId?: ParameterID
  withBreadcrumbNames?: boolean
  withCustomSeparator?: ReactNode
  withInsuranceInfo?: boolean
}

export function TextItemCategory({
  categoryId,
  size,
  withBreadcrumbNames = false,
  ...props
}: TextItemCategoryProps) {
  const { getCategoriesBreadcrumb, getCategoryById, isLoading } =
    useItemCategoriesInfo()

  if (!categoryId) {
    return null
  }

  const category = getCategoryById(categoryId)

  if (isLoading) {
    return (
      <Flex align='center' gap='sm'>
        <Skeleton h={20} radius='xl' w={100} />
        <Skeleton h={20} radius='xl' w={40} />
        <Skeleton h={20} radius='xl' w={60} />
      </Flex>
    )
  }

  if (!category) {
    return null
  }

  return (
    <Flex align='center' gap='xs' wrap='wrap'>
      <Text
        data-testid='text-item-category'
        size={size}
        truncate
        {...props}
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          flexWrap: 'wrap',
          gap: 'var(--mantine-spacing-xs)',
        }}
      >
        {withBreadcrumbNames
          ? getCategoriesBreadcrumb(categoryId).map((cat, index) =>
              index > 0 ? (
                <>
                  <Icon
                    color={props.color ?? 'gray.5'}
                    icon={faCaretRight}
                    size='xs'
                  />
                  <Text fw={props.fw} c={props.c} size={size}>
                    {cat.name}
                  </Text>
                </>
              ) : (
                <Text fw={props.fw} c={props.c} size={size} key={index}>
                  {cat.name}
                </Text>
              )
            )
          : category.name}
      </Text>
    </Flex>
  )
}
